import { Button, Divider, Typography } from "@suraasa/placebo-ui"
import api from "api"
import { LearningItem } from "api/resources/learningItems/types"
import { getCourseVideoPoster } from "components/VideoPlayer/getPoster"
import VideoPlayerV2, { VideoPlayerHandle } from "components/VideoPlayerV2"
import { ReactComponent as NoteAdd } from "features/LearningModule/assets/icons/note-add.svg"
import debounce from "lodash/debounce"
import { useContext, useEffect, useRef, useState } from "react"
import { isIOS } from "react-device-detect"
import { useParams } from "react-router-dom"
import { BROWSER_STORAGE_KEYS } from "utils/constants"
import useCurrentCountry from "utils/hooks/useCurrentCountry"
import { useEnrollments } from "utils/hooks/useEnrollments"
import { UserPreferenceManager } from "utils/userPreferenceManager"

import { LearningModuleContext } from "../../context"
import { useUpdateItemStatus } from "../../hooks/useUpdateItemStatus"
import AddNoteDialog from "../Notes/AddNoteDialog"
import Notes from "../Notes/Notes"
import fallBackPoster from "./assets/fallback-poster.webp"

type Props = {
  item: LearningItem
}

const { showVideoSubtitles, videoLanguagePreference } = BROWSER_STORAGE_KEYS

const Video = ({ item }: Props) => {
  const { slug } = useParams() as { slug: string }
  const { enrollments } = useEnrollments()
  const courseId = enrollments?.find(x => x.slug === slug)?.uuid
  const courseName = enrollments?.find(x => x.slug === slug)?.name
  const isVideo = item.learningContentType === "video"

  if (!isVideo) {
    throw new Error("Video component can only be used for video content type")
  }

  const { updateItemStatus } = useUpdateItemStatus(item)

  const {
    addVideoBookmarkInState,
    removeVideoBookmarkFromState,
    enableVideoNextButton,
  } = useContext(LearningModuleContext)

  const [openNotesDialog, setOpenNotesDialog] = useState(false)

  const videoPlayerRef = useRef<VideoPlayerHandle>(null)

  const [currentTimestamp, setCurrentTimestamp] = useState(0)

  const lastSaved = useRef(0)
  const savedSpeed =
    parseFloat(
      UserPreferenceManager.get(
        BROWSER_STORAGE_KEYS.learningVideoPreferredPlaybackSpeed
      )
    ) || 1

  const country = useCurrentCountry()

  const [videoWasPausedWhenDialogOpened, setVideoWasPausedWhenDialogOpened] =
    useState(false)

  const handleNoteDialogClose = () => {
    if (!videoWasPausedWhenDialogOpened) {
      videoPlayerRef.current?.play()
      setVideoWasPausedWhenDialogOpened(false)
    }
    setOpenNotesDialog(false)
  }

  const handleAddNoteDialogOpen = () => {
    if (videoPlayerRef.current) {
      const alreadyPaused = videoPlayerRef.current.paused
      setVideoWasPausedWhenDialogOpened(alreadyPaused)
      if (!alreadyPaused) {
        videoPlayerRef.current.pause()
      }

      setCurrentTimestamp(videoPlayerRef.current.currentTime)
      setOpenNotesDialog(true)
    }
  }

  const gotoTimeStamp = (time: number) => {
    if (videoPlayerRef.current) videoPlayerRef.current.currentTime = time
  }

  const handleDelete = async (id: string) => {
    try {
      await api.learningItems.removeVideoBookmark({
        urlParams: {
          id,
        },
      })
      removeVideoBookmarkFromState(id)
    } catch (error) {
      console.error(error)
    }
  }

  function onVideoEnded() {
    try {
      if (
        (document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.mozFullScreenElement) &&
        document.exitFullscreen
      ) {
        document.exitFullscreen()
      }
    } catch (error) {
      console.log(error)
    }
    // else if (document.webkitExitFullscreen) document.webkitExitFullscreen()
    // else if (document.mozCancelFullScreen) document.mozCancelFullScreen()
    // else if (document.msExitFullscreen) document.msExitFullscreen()

    // Clear progress if video is completed
    const progressMap =
      UserPreferenceManager.get(BROWSER_STORAGE_KEYS.learningVideoProgress) ||
      {}
    if (isVideo && progressMap[item.id]) {
      delete progressMap[item.id]
      UserPreferenceManager.set(
        BROWSER_STORAGE_KEYS.learningVideoProgress,
        progressMap
      )
    }
  }

  useEffect(() => {
    const videoPlayerRefCurrent = videoPlayerRef.current
    if (!videoPlayerRefCurrent) return

    videoPlayerRefCurrent.addEventListener("ended", onVideoEnded)

    if (!isNaN(savedSpeed)) {
      videoPlayerRefCurrent.playbackRate = savedSpeed
    }

    const onRateChange = () => {
      UserPreferenceManager.set(
        BROWSER_STORAGE_KEYS.learningVideoPreferredPlaybackSpeed,
        videoPlayerRefCurrent?.playbackRate.toString()
      )
    }

    videoPlayerRefCurrent.addEventListener("ratechange", onRateChange)

    return () => {
      videoPlayerRefCurrent.removeEventListener("ended", onVideoEnded)
      videoPlayerRefCurrent.removeEventListener("ratechange", onRateChange)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const videoPlayerRefCurrent = videoPlayerRef.current
    if (!videoPlayerRefCurrent) return

    const onVideoLoaded = () => {
      const player = videoPlayerRef.current

      const progressMap =
        UserPreferenceManager.get(BROWSER_STORAGE_KEYS.learningVideoProgress) ||
        {}

      const lastSeen = progressMap[item.id]
      if (
        player &&
        typeof lastSeen === "number" &&
        lastSeen < item.duration - 5
      ) {
        setTimeout(() => {
          player.currentTime = lastSeen
        }, 500)
      }
    }

    videoPlayerRefCurrent.addEventListener("loadeddata", onVideoLoaded)

    return () => {
      videoPlayerRefCurrent.removeEventListener("loadeddata", onVideoLoaded)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!videoPlayerRef.current) return

    const player = videoPlayerRef.current

    const onPlay = debounce(() => {
      updateItemStatus()
    }, 100)

    const onTimeUpdate = () => {
      const currentTime = player.currentTime
      const duration = player.duration

      if (!duration) return

      // Save every 3 seconds
      if (Math.abs(currentTime - lastSaved.current) > 3) {
        lastSaved.current = currentTime
        const progressMap =
          UserPreferenceManager.get(
            BROWSER_STORAGE_KEYS.learningVideoProgress
          ) || {}

        UserPreferenceManager.set(BROWSER_STORAGE_KEYS.learningVideoProgress, {
          ...progressMap,
          [item.id]: Math.floor(currentTime),
        })
      }

      if (currentTime >= duration * 0.9) {
        console.info("> Allow next")
        enableVideoNextButton()
      }
    }

    player.addEventListener("playing", onPlay)
    player.addEventListener("timeupdate", onTimeUpdate)

    return () => {
      player.removeEventListener("playing", onPlay)
      player.removeEventListener("timeupdate", onTimeUpdate)

      // Save timestamp on unmount
      const currentTime = player.currentTime
      const duration = player.duration

      if (currentTime && duration && currentTime < duration - 5) {
        const progressMap =
          UserPreferenceManager.get(
            BROWSER_STORAGE_KEYS.learningVideoProgress
          ) || {}

        UserPreferenceManager.set(BROWSER_STORAGE_KEYS.learningVideoProgress, {
          ...progressMap,
          [item.id]: Math.floor(currentTime),
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateItemStatus])

  const { title, url } = item
  if (!url) throw new Error("Video url is not defined")

  const languages = item.languagesAvailable?.url

  const getDefaultLanguageId = () => {
    if (!languages) return ""

    const findByLocale = (localeCode: string) =>
      languages.find(x => x.localeCode === localeCode)?.uuid

    // Non-listed isoCodes fall back to en_GB via the ?? below.
    const countryLocales: Record<string, string[]> = {
      IN: ["en", "en_GB"],
      US: ["en_US"],
    }

    const preferredLocale = UserPreferenceManager.get(videoLanguagePreference)
    const localePriority = [
      preferredLocale,
      ...(country?.isoCode ? countryLocales[country.isoCode] ?? ["en_GB"] : []),
    ].filter(Boolean)

    for (const locale of localePriority) {
      const languageId = findByLocale(locale)
      if (languageId) return languageId
    }

    return languages[0]?.uuid ?? ""
  }

  const [currentLanguageId, setCurrentLanguageId] = useState(
    getDefaultLanguageId()
  )

  const [showSubtitle, setShowSubtitle] = useState<boolean>(
    UserPreferenceManager.get(showVideoSubtitles) === "true"
  )

  useEffect(() => {
    getCourseVideoPoster(title, { courseName }).then(result => {
      if (videoPlayerRef.current) {
        videoPlayerRef.current.setPoster(result)
      }
    })

    const savedPref = UserPreferenceManager.get(showVideoSubtitles) === "true"
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setShowSubtitle(savedPref)
  }, [item.url, title, courseName])

  const handleLanguageChange = languageId => {
    setCurrentLanguageId(languageId)

    const selectedLanguage = item.languagesAvailable?.url.find(
      lang => lang.uuid === languageId
    )

    if (selectedLanguage?.localeCode) {
      UserPreferenceManager.set(
        videoLanguagePreference,
        selectedLanguage.localeCode
      )
    }
  }

  return (
    <div className="mb-5">
      {courseId && (
        <AddNoteDialog
          courseId={courseId}
          videoId={item.uuid}
          open={openNotesDialog}
          timestamp={currentTimestamp}
          onAdd={data => {
            addVideoBookmarkInState(data)
            handleNoteDialogClose()
          }}
          handleClose={handleNoteDialogClose}
        />
      )}

      {item.url && (
        <VideoPlayerV2
          ref={videoPlayerRef}
          sources={languages ?? []}
          defaultLanguageId={currentLanguageId}
          showSubtitles={showSubtitle}
          autoPlay={!isIOS}
          poster={fallBackPoster}
          onLanguageChange={languageId => {
            handleLanguageChange(languageId)
          }}
          onSubtitleChange={isEnabled => {
            setShowSubtitle(isEnabled)
            UserPreferenceManager.set(showVideoSubtitles, String(isEnabled))
          }}
          style={{
            borderRadius: "22px",
            overflow: "hidden",
          }}
        />
      )}

      <Typography variant="title3" className="mt-2">
        {title}
      </Typography>
      <Divider
        className="my-3 hidden bg-onSurface-200 sm:block"
        weight="light"
      />
      <div className="my-1.5 flex items-center justify-between sm:mt-0">
        <Typography variant="strong">Notes</Typography>
        <Button
          variant="text"
          startAdornment={<NoteAdd className="fill-primary-500" />}
          onClick={handleAddNoteDialogOpen}
        >
          Add note
        </Button>
      </div>

      <Notes
        gotoTimeStamp={gotoTimeStamp}
        notes={item.bookmarkVideo}
        onDelete={handleDelete}
      />
    </div>
  )
}

export default Video
